import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  Copy,
  Download,
  Upload,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Package,
  Tag,
  Box,
  AlertTriangle,
  Check,
  X,
  Loader2,
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cn, formatCurrency, formatNumber } from '../../lib/utils'
import { productsApi, queryKeys, type Product, type ProductListParams } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { Dropdown } from '../../components/ui/Dropdown'
import { Skeleton } from '../../components/ui/Skeleton'
import { Pagination } from '../../components/ui/Pagination'
import { useUIStore } from '../../stores/uiStore'
import { usePermissions } from '../../stores/authStore'
import toast from 'react-hot-toast'

// Column definitions
const columns = [
  { key: 'image', header: '', width: '50px' },
  { key: 'sku', header: 'SKU', width: '130px' },
  { key: 'name', header: 'نام کالا', minWidth: '200px' },
  { key: 'category', header: 'دسته‌بندی', width: '140px' },
  { key: 'brand', header: 'برند', width: '120px' },
  { key: 'stock', header: 'موجودی', width: '120px' },
  { key: 'price', header: 'قیمت فروش', width: '140px' },
  { key: 'status', header: 'وضعیت', width: '100px' },
  { key: 'actions', header: 'عملیات', width: '100px' },
]

export default function ProductsPage() {
  const { hasPermission } = usePermissions()
  const { modals, openModal, closeModal } = useUIStore()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<ProductListParams>({
    page: 1,
    page_size: 20,
    sort_by: 'created_at',
    sort_order: 'desc',
  })
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'created_at',
    direction: 'desc',
  })
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [deletedId, setDeletedId] = useState<string | null>(null)

  // Fetch products
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.products(filters),
    queryFn: () => productsApi.list(filters),
    placeholderData: (prev) => prev,
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => productsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products() })
      toast.success('کالا با موفقیت حذف شد')
      closeModal('delete-confirm')
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.detail || 'حذف ناموفق بود')
    },
  })

  // Bulk delete
  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => productsApi.bulkDelete(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products() })
      toast.success(`${selectedIds.length} کالا حذف شدند`)
      setSelectedIds([])
      closeModal('bulk-delete-confirm')
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.detail || 'حذف گروهی ناموفق بود')
    },
  })

  // Toggle selection
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const toggleSelectAll = () => {
    if (selectedIds.length === (data?.items.length || 0)) {
      setSelectedIds([])
    } else {
      setSelectedIds(data?.items.map(p => p.id) || [])
    }
  }

  // Handle sort
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
    setFilters(prev => ({
      ...prev,
      sort_by: key,
      sort_order: direction,
      page: 1,
    }))
  }

  // Handle page change
  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setFilters(prev => ({ ...prev, search, page: 1 }))
  }

  // Handle filter change
  const handleFilterChange = (key: keyof ProductListParams, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }))
  }

  const totalPages = data?.total_pages || 1
  const totalItems = data?.total || 0

  // Dropdown items for each row
  const getRowDropdownItems = (product: Product) => [
    { label: 'مشاهده', icon: Eye, onClick: () => window.open(`/inventory/products/${product.id}`, '_blank') },
    { label: 'ویرایش', icon: Edit, onClick: () => window.open(`/inventory/products/${product.id}/edit`, '_blank'), disabled: !hasPermission('product', 'update') },
    { label: 'کپی SKU', icon: Copy, onClick: () => navigator.clipboard.writeText(product.sku) },
    { type: 'divider' },
    { label: 'حذف', icon: Trash2, className: 'text-danger-600', onClick: () => { setDeletedId(product.id); openModal('delete-confirm') }, disabled: !hasPermission('product', 'delete') },
  ]

  // Toolbar dropdown items
  const toolbarItems = [
    { label: 'وارد کردن اکسل', icon: Upload, onClick: () => openModal('import') },
    { label: 'خروجی اکسل', icon: Download, onClick: () => productsApi.export(filters).then(blob => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `products-${new Date().toISOString().split('T')[0]}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
    }) },
    { type: 'divider' },
    { label: 'حذف انتخاب‌ها', icon: Trash2, className: 'text-danger-600', onClick: () => selectedIds.length > 0 && openModal('bulk-delete-confirm'), disabled: selectedIds.length === 0 },
  ]

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">مدیریت محصولات</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1 text-sm">
            {totalItems.toLocaleString('fa-IR')} کالا ثبت شده
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Button
            variant="outline"
            onClick={() => openModal('import')}
            disabled={!hasPermission('product', 'create')}
            className="whitespace-nowrap text-xs sm:text-sm px-3 sm:px-4"
          >
            <Upload className="w-4 h-4 shrink-0" />
            <span>وارد کردن</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => productsApi.export(filters).then(blob => {
              const url = URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = `products-${new Date().toISOString().split('T')[0]}.xlsx`
              a.click()
              URL.revokeObjectURL(url)
            })}
            className="whitespace-nowrap text-xs sm:text-sm px-3 sm:px-4"
          >
            <Download className="w-4 h-4 shrink-0" />
            <span>خروجی</span>
          </Button>
          <Link to="/inventory/products/new">
            <Button disabled={!hasPermission('product', 'create')} className="whitespace-nowrap text-xs sm:text-sm px-3.5 sm:px-4 shadow-sm">
              <Plus className="w-4 h-4 shrink-0" />
              <span>کالا جدید</span>
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* Filters & Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card p-4"
      >
        <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              placeholder="جستجو در نام، SKU، بارکد..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-10"
            />
          </div>

          <Select
            value={filters.category_id || ''}
            onChange={(v) => handleFilterChange('category_id', v || undefined)}
            options={[
              { value: '', label: 'همه دسته‌بندی‌ها' },
            ]}
            placeholder="دسته‌بندی"
            className="w-full sm:w-44"
          />

          <Select
            value={filters.brand_id || ''}
            onChange={(v) => handleFilterChange('brand_id', v || undefined)}
            options={[
              { value: '', label: 'همه برندها' },
            ]}
            placeholder="برند"
            className="w-full sm:w-36"
          />

          <Select
            value={filters.is_active?.toString() || ''}
            onChange={(v) => handleFilterChange('is_active', v === 'true' ? true : v === 'false' ? false : undefined)}
            options={[
              { value: '', label: 'همه وضعیت‌ها' },
              { value: 'true', label: 'فعال' },
              { value: 'false', label: 'غیرفعال' },
            ]}
            placeholder="وضعیت"
            className="w-full sm:w-32"
          />

          <Select
            value={filters.type || ''}
            onChange={(v) => handleFilterChange('type', v || undefined)}
            options={[
              { value: '', label: 'همه انواع' },
              { value: 'simple', label: 'ساده' },
              { value: 'variant', label: 'متغییر' },
              { value: 'bundle', label: 'بندل' },
              { value: 'service', label: 'خدماتی' },
              { value: 'digital', label: 'دیجیتال' },
            ]}
            placeholder="نوع کالا"
            className="w-full sm:w-32"
          />

          <Dropdown
            trigger={
              <Button variant="outline" className="whitespace-nowrap">
                <Filter className="w-4 h-4 ml-1" />
                فیلترها
              </Button>
            }
            items={toolbarItems}
          />
        </form>
      </motion.div>

      {/* Data Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card overflow-hidden"
      >
        {isLoading ? (
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    {columns.map(col => <th key={col.key} style={{ width: col.width }}>{col.header}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td><Skeleton className="w-10 h-10 rounded" /></td>
                      <td><Skeleton className="w-24 h-4" /></td>
                      <td><Skeleton className="w-40 h-4" /></td>
                      <td><Skeleton className="w-28 h-4" /></td>
                      <td><Skeleton className="w-24 h-4" /></td>
                      <td><Skeleton className="w-24 h-4" /></td>
                      <td><Skeleton className="w-28 h-4" /></td>
                      <td><Skeleton className="w-24 h-4" /></td>
                      <td><Skeleton className="w-20 h-8" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertTriangle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
            <p className="text-danger-600 dark:text-danger-400">خطا در بارگذاری محصولات</p>
            <Button variant="outline" onClick={() => refetch()} className="mt-4">
              <Loader2 className="w-4 h-4 ml-1" />
              تلاش مجدد
            </Button>
          </div>
        ) : data?.items.length === 0 ? (
          <div className="p-12 text-center empty-state">
            <Package className="w-16 h-16 empty-state-icon" />
            <h3 className="empty-state-title">هیچ کالایی یافت نشد</h3>
            <p className="empty-state-description">
              {search || Object.values(filters).some(v => v) ? 'تغییر فیلترها را امتحان کنید' : 'اولین کالا را اضافه کنید'}
            </p>
            {(!search && !Object.values(filters).some(v => v)) && (
              <Link to="/inventory/products/new">
                <Button className="mt-4"><Plus className="w-4 h-4 ml-1" /> افزودن کالا</Button>
              </Link>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    {columns.map(col => (
                      <th
                        key={col.key}
                        style={{ width: col.width, minWidth: col.minWidth }}
                        className={col.key !== 'actions' && col.key !== 'image' && col.key !== 'status' ? 'cursor-pointer' : ''}
                        onClick={() => col.key !== 'actions' && col.key !== 'image' && col.key !== 'status' && handleSort(col.key)}
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span>{col.header}</span>
                          {col.key !== 'actions' && col.key !== 'image' && col.key !== 'status' && (
                            <span className="text-xs">
                              {sortConfig.key === col.key ? (sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ChevronDown className="w-3 h-3 text-neutral-300" />}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data?.items || []).map((product, index) => (
                    <motion.tr
                      key={product.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className={selectedIds.includes(product.id) ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/50'}
                    >
                      <td className="p-2">
                        <div className="w-10 h-10 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center overflow-hidden">
                          {product.images?.[0] ? (
                            <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                          ) : (
                            <Package className="w-5 h-5 text-neutral-400" />
                          )}
                        </div>
                      </td>
                      <td className="font-mono text-sm text-neutral-700 dark:text-neutral-300">{product.sku}</td>
                      <td className="font-medium text-neutral-900 dark:text-white truncate max-w-xs">{product.name}</td>
                      <td className="text-sm text-neutral-600 dark:text-neutral-400">{product.category?.name || '-'}</td>
                      <td className="text-sm text-neutral-600 dark:text-neutral-400">{product.brand?.name || '-'}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            'font-mono font-medium',
                            product.current_stock === 0 ? 'text-danger-600 dark:text-danger-400' :
                            product.current_stock <= (product.min_stock || 0) ? 'text-warning-600 dark:text-warning-400' :
                            'text-neutral-900 dark:text-white'
                          )}>
                            {formatNumber(product.current_stock)}
                          </span>
                          {product.current_stock <= (product.min_stock || 0) && product.min_stock > 0 && (
                            <AlertTriangle className="w-3.5 h-3.5 text-warning-500" title={`حداقل: ${product.min_stock}`} />
                          )}
                        </div>
                      </td>
                      <td className="font-mono text-sm text-neutral-700 dark:text-neutral-300">
                        {formatCurrency(product.sale_price || 0)}
                      </td>
                      <td>
                        <Badge variant={product.is_active ? 'success' : 'neutral'} className="text-xs">
                          {product.is_active ? 'فعال' : 'غیرفعال'}
                        </Badge>
                      </td>
                      <td>
                        <Dropdown
                          trigger={
                            <button className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          }
                          items={getRowDropdownItems(product)}
                        />
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                نمایش {((filters.page - 1) * filters.page_size) + 1} تا {Math.min(filters.page * filters.page_size, totalItems)} از {totalItems.toLocaleString('fa-IR')}
              </div>
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                showPageSize
                pageSize={filters.page_size}
                onPageSizeChange={(size) => setFilters(prev => ({ ...prev, page_size: size, page: 1 }))}
              />
            </div>
          </>
        )}
      </motion.div>

      {/* Modals */}
      <Modal
        isOpen={modals['delete-confirm']}
        onClose={() => { closeModal('delete-confirm'); setDeletedId(null) }}
        title="حذف کالا"
        size="sm"
      >
        <p className="text-neutral-600 dark:text-neutral-400">
          آیا از حذف این کالا مطمئن هستید؟ این عملیات قابل بازگشت نیست.
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => { closeModal('delete-confirm'); setDeletedId(null) }}>
            انصراف
          </Button>
          <Button
            variant="danger"
            onClick={() => deletedId && deleteMutation.mutate(deletedId)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                در حال حذف...
              </>
            ) : (
              'حذف'
            )}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={modals['bulk-delete-confirm']}
        onClose={() => closeModal('bulk-delete-confirm')}
        title="حذف گروهی"
        size="sm"
      >
        <p className="text-neutral-600 dark:text-neutral-400">
          آیا از حذف <strong>{selectedIds.length}</strong> کالا انتخاب شده مطمئن هستید؟
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => closeModal('bulk-delete-confirm')}>
            انصراف
          </Button>
          <Button
            variant="danger"
            onClick={() => bulkDeleteMutation.mutate(selectedIds)}
            disabled={bulkDeleteMutation.isPending}
          >
            {bulkDeleteMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                در حال حذف...
              </>
            ) : (
              'حذف همه'
            )}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={modals['import']}
        onClose={() => closeModal('import')}
        title="وارد کردن محصولات از اکسل"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-neutral-600 dark:text-neutral-400">
            فایل اکسل (.xlsx) یا CSV را انتخاب کنید. ستون‌های مورد نیاز: SKU، نام، قیمت فروش، دسته‌بندی، برند.
          </p>
          <div className="border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-xl p-8 text-center hover:border-primary-500 transition-colors">
            <Upload className="w-10 h-10 text-neutral-400 mx-auto mb-2" />
            <p className="text-neutral-600 dark:text-neutral-400">فایل را اینجا رها کنید یا کلیک کنید</p>
            <input type="file" accept=".xlsx,.csv" className="hidden" id="import-file" />
            <label htmlFor="import-file" className="mt-2 inline-block">
              <Button variant="outline">انتخاب فایل</Button>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => closeModal('import')}>
              انصراف
            </Button>
            <Button disabled>
              آپلود و پردازش
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}